export interface MonthlyBreakdown {
  month: string; // e.g. "Jul", "Aug"
  value: number | null;
}

export interface AdjustmentItem {
  id: string;
  label: string;
  refNumber?: number;
  isSubtotal?: boolean;
  isSectionHeader?: boolean;
  isItalic?: boolean;
  mapping?: string;
  account?: string;
  subAccount?: string;
  reason?: string;
  values: Record<string, number | string | null>;
  monthlyBreakdown?: Record<string, MonthlyBreakdown[]>; // keyed by period e.g. "FY23"
  status: 'pending' | 'accepted' | 'rejected';
  isNew?: boolean;
}

export interface AdjustmentTable {
  id: string;
  title: string;
  unit: string;
  periods: string[];
  items: AdjustmentItem[];
}

const MONTHS_FY23 = ['Jul-22','Aug-22','Sep-22','Oct-22','Nov-22','Dec-22','Jan-23','Feb-23','Mar-23','Apr-23','May-23','Jun-23'];
const MONTHS_FY24 = ['Jul-23','Aug-23','Sep-23','Oct-23','Nov-23','Dec-23','Jan-24','Feb-24','Mar-24','Apr-24','May-24','Jun-24'];
const MONTHS_FY25 = ['Jul-24','Aug-24','Sep-24','Oct-24','Nov-24','Dec-24','Jan-25','Feb-25','Mar-25','Apr-25','May-25','Jun-25'];

function getMonthsForPeriod(period: string): string[] {
  if (period === 'FY23') return MONTHS_FY23;
  if (period === 'FY24') return MONTHS_FY24;
  if (period === 'FY25') return MONTHS_FY25;
  return MONTHS_FY24;
}

function buildMonthly(period: string, total: number | null): MonthlyBreakdown[] {
  const months = getMonthsForPeriod(period);
  if (total === null) return months.map(m => ({ month: m, value: null }));
  const perMonth = Math.round(total / months.length);
  return months.map((m, i) => ({
    month: m,
    value: i === months.length - 1 ? total - perMonth * (months.length - 1) : perMonth,
  }));
}

export const mockNetDebtAdjustments: AdjustmentTable = {
  id: 'net-debt',
  title: 'Net Debt Adjustments',
  unit: '$000',
  periods: ['FY23', 'FY24', 'FY25'],
  items: [
    {
      id: 'nd-1', label: 'Placeholder — pending Q&A completion', refNumber: 1,
      mapping: 'Balance Sheet', account: 'Borrowings', subAccount: 'Term Loans',
      reason: 'Shareholder loan of $350k classified as equity but has fixed repayment terms. Should be reclassified as debt-like for enterprise value bridge.',
      values: { 'FY23': null, 'FY24': null, 'FY25': null },
      monthlyBreakdown: {
        'FY23': buildMonthly('FY23', null),
        'FY24': buildMonthly('FY24', null),
        'FY25': buildMonthly('FY25', null),
      },
      status: 'pending',
    },
  ],
};

export const mockQoEAdjustments: AdjustmentTable = {
  id: 'qoe',
  title: 'Quality of Earnings Adjustments',
  unit: '$000',
  periods: ['FY23', 'FY24', 'FY25'],
  items: [
    { id: 'qoe-1', label: 'Reported EBITDA', isSubtotal: true, values: { 'FY23': 1250, 'FY24': 1380, 'FY25': 1520 }, status: 'pending' },
    { id: 'qoe-2', label: 'Normalisation adjustments', isSectionHeader: true, isItalic: true, values: {}, status: 'pending' },
    {
      id: 'qoe-3', label: 'One-off subsidy removal', refNumber: 1,
      mapping: 'Revenue', account: 'Other Income', subAccount: 'Government Grants',
      reason: 'One-off government subsidy received in FY24 ($1.3m) not expected to recur. Confirmed by management as COVID-era relief payment.',
      values: { 'FY23': null, 'FY24': -1300, 'FY25': null },
      monthlyBreakdown: {
        'FY23': buildMonthly('FY23', null),
        'FY24': buildMonthly('FY24', -1300),
        'FY25': buildMonthly('FY25', null),
      },
      status: 'pending',
    },
    { id: 'qoe-4', label: 'Total adjustments', values: { 'FY23': null, 'FY24': -1300, 'FY25': null }, status: 'pending' },
    { id: 'qoe-5', label: 'Normalised EBITDA', isSubtotal: true, values: { 'FY23': 1250, 'FY24': 80, 'FY25': 1520 }, status: 'pending' },
  ],
};

export const mockQoNWCAdjustments: AdjustmentTable = {
  id: 'qonwc',
  title: 'Quality of Net Working Capital Adjustments',
  unit: '$000',
  periods: ['FY23', 'FY24', 'FY25'],
  items: [
    {
      id: 'nwc-1', label: 'Placeholder — pending Q&A completion', refNumber: 1,
      mapping: 'Working Capital', account: 'Trade Receivables', subAccount: 'Current',
      reason: 'Receivables days increased from 42 to 58 over the period. Awaiting management explanation on collection issues and customer credit terms.',
      values: { 'FY23': null, 'FY24': null, 'FY25': null },
      monthlyBreakdown: {
        'FY23': buildMonthly('FY23', null),
        'FY24': buildMonthly('FY24', null),
        'FY25': buildMonthly('FY25', null),
      },
      status: 'pending',
    },
  ],
};
